import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

const TRANSFER_FEE = 15.0;

/**
 * Dialog for a dog owner (seller) to initiate an ownership transfer.
 * The seller does NOT pay the fee — the buyer pays the $15 administrative
 * fee at the moment they accept the transfer (see Transfers.jsx for the
 * buyer's pay-and-accept button).
 *
 * This dialog therefore POSTs directly to /transfers (free) instead of
 * routing through /billing/checkout.
 *
 * If the seller has 2FA enabled, the dialog reveals a TOTP field
 * because POST /transfers is gated by `require_totp` server-side.
 */
export default function TransferDialog({ open, onClose, dogId, dogName }) {
  const [toEmail, setToEmail] = useState("");
  const [toName, setToName] = useState("");
  const [message, setMessage] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Detect 2FA on dialog open so the field renders only when needed.
  useEffect(() => {
    if (!open) return;
    api.get("/auth/2fa/status")
      .then((r) => setNeeds2fa(!!r.data?.enabled))
      .catch(() => setNeeds2fa(false));
  }, [open]);

  const reset = () => {
    setToEmail("");
    setToName("");
    setMessage("");
    setTotpCode("");
  };

  const submit = async () => {
    if (!toEmail.trim()) {
      toast.error("Buyer email is required.");
      return;
    }
    if (needs2fa && !totpCode.trim()) {
      toast.error("Enter your authenticator code to authorise this transfer.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/transfers", {
        dog_id: dogId,
        initiated_by: "seller",
        to_email: toEmail.trim().toLowerCase(),
        to_name: toName.trim(),
        message: message.trim(),
        ...(needs2fa ? { totp_code: totpCode.trim() } : {}),
      });
      toast.success(
        `Transfer request sent. ${toEmail.trim()} will pay the $${TRANSFER_FEE.toFixed(2)} fee on acceptance.`
      );
      reset();
      onClose(true);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose(false);
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer ownership of {dogName}</DialogTitle>
          <DialogDescription>
            The buyer pays the ${TRANSFER_FEE.toFixed(2)} administrative fee
            when they accept. A registry admin will review and approve the
            transfer once the buyer pays.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Buyer email *
            </Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="buyer@example.com"
              className="mt-1 rounded-lg"
              data-testid="transfer-to-email"
            />
            <p className="text-xs text-[#6B6B6B] mt-1">
              If the buyer doesn't have an account yet, they'll be able to
              accept and pay after registering with this email.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Buyer name (optional)
            </Label>
            <Input
              value={toName}
              onChange={(e) => setToName(e.target.value)}
              placeholder="Full name"
              className="mt-1 rounded-lg"
              data-testid="transfer-to-name"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Message to buyer (optional)
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="mt-1 rounded-lg"
              placeholder="Health notes, pick-up info, etc."
              data-testid="transfer-message"
            />
          </div>

          {needs2fa && (
            <div className="rounded-md border border-[#E5D08A] bg-[#FBF7EC] p-3" data-testid="transfer-2fa-field">
              <div className="flex items-center gap-1.5 mb-2">
                <ShieldCheck size={14} weight="duotone" className="text-[#8A6A00]" />
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A6A00]">
                  Authenticator code required
                </Label>
              </div>
              <Input
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="123456 or recovery code"
                inputMode="text"
                autoComplete="one-time-code"
                className="rounded-lg font-mono"
                data-testid="transfer-totp-input"
              />
              <p className="text-[11px] text-[#5C4A00] mt-1.5">
                You enabled 2FA on this account. Confirm with a fresh
                code from your authenticator app to authorise the
                transfer.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose(false);
            }}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="transfer-submit-btn"
          >
            {submitting ? "Sending…" : "Send transfer request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
